import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  MOCK_ADMIN_SESSION,
  type AdminSession,
  type AdminSessionState,
} from "@/lib/auth/admin-session";
import {
  clearPersistedAdminSession,
  getPersistedRefreshToken,
  setPersistedAccessToken,
  setPersistedRefreshToken,
} from "@/lib/auth/admin-session-storage";
import {
  getInMemoryAccessToken,
  setInMemoryAccessToken,
} from "@/lib/auth/admin-token-storage";
import {
  resetAdminSessionExpiredNotified,
  setAdminSessionExpiredHandler,
  type AdminLoginRedirectReason,
} from "@/lib/auth/admin-session-expired";
import { restoreAdminSession, adminMeToSession } from "@/lib/auth/restore-admin-session";
import { refreshAdminAccessToken } from "@/lib/auth/refresh-admin-tokens";
import { notifyAdminSessionExpired } from "@/lib/auth/admin-session-expired";
import { APP_CONFIG } from "@/lib/constants/app-config";
import { setAdminAuthTokenGetter } from "@/lib/api/admin-real-client";
import {
  getAdminMe,
  isAdminMfaChallengeResponse,
  postAdminLogin,
  postAdminLogout,
  postAdminMfaVerify,
  type AdminLoginResponse,
} from "@/lib/api/admin-auth-api";
import { isAdminMockMode } from "@/lib/api/admin-contract";
import type { AdminPermission } from "@/lib/permissions/permissions";
import { hasPermission } from "@/lib/permissions/permission-check";

/**
 * Admin auth context.
 *
 *   mock  ── seeded session; no network.
 *   real  ── login stores tokens in memory + sessionStorage (tab scope),
 *           restores session on page load via persisted access token or
 *           /admin/auth/refresh when available.
 */
/**
 * Result of a credential sign-in.
 *   authenticated  ── password OK, no MFA (or mock mode): session is live.
 *   mfa_required   ── password OK but the admin has MFA enabled. NO session,
 *                     NO tokens yet — the caller must render the MFA form and
 *                     call `completeMfaChallenge` with the returned challenge.
 */
export type AdminSignInOutcome =
  | { status: "authenticated" }
  | { status: "mfa_required"; challengeId: string; expiresIn: number };

interface AdminAuthContextValue {
  state: AdminSessionState;
  session: AdminSession | null;
  loginRedirectReason: AdminLoginRedirectReason;
  mode: "mock" | "real";
  signIn: (session: AdminSession) => void;
  signInWithCredentials: (input: {
    email: string;
    password: string;
  }) => Promise<AdminSignInOutcome>;
  /**
   * Step 2 of an MFA login. Verifies the code against the challenge and, only
   * on success, promotes the session to authenticated. Throws on an invalid /
   * expired code (the caller keeps the challenge visible and shows the error).
   */
  completeMfaChallenge: (input: {
    challengeId: string;
    code: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  hasPermission: (permission: AdminPermission) => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export interface AdminAuthProviderProps {
  children: ReactNode;
  initialSession?: AdminSession | null;
}

function applyLoginTokens(login: AdminLoginResponse): void {
  setInMemoryAccessToken(login.access_token);
  setPersistedAccessToken(login.access_token);
  setPersistedRefreshToken(login.refresh_token ?? null);
}

function clearAllTokens(ref: { current: string | null }): void {
  setInMemoryAccessToken(null);
  ref.current = null;
  clearPersistedAdminSession();
}

export function AdminAuthProvider({ children, initialSession }: AdminAuthProviderProps) {
  const mode: "mock" | "real" = isAdminMockMode ? "mock" : "real";
  const queryClient = useQueryClient();

  const [loginRedirectReason, setLoginRedirectReason] =
    useState<AdminLoginRedirectReason>("unauthorized");

  const [state, setState] = useState<AdminSessionState>(() => {
    if (mode === "mock") {
      const seed = initialSession ?? MOCK_ADMIN_SESSION;
      return { status: "authenticated", session: seed };
    }
    if (initialSession) {
      return { status: "authenticated", session: initialSession };
    }
    return { status: "loading" };
  });

  const refreshTokenRef = useRef<string | null>(null);
  const restoreStartedRef = useRef(false);

  useEffect(() => {
    if (mode === "mock") return;
    setAdminAuthTokenGetter(() => getInMemoryAccessToken());
    return () => setAdminAuthTokenGetter(null);
  }, [mode]);

  useEffect(() => {
    if (mode === "mock") return;

    setAdminSessionExpiredHandler(() => {
      clearAllTokens(refreshTokenRef);
      queryClient.clear();
      setLoginRedirectReason("session_expired");
      setState({ status: "unauthenticated" });
    });

    return () => setAdminSessionExpiredHandler(null);
  }, [mode, queryClient]);

  // Proactive background refresh: while authenticated in real mode, silently
  // renew the access token on an interval so an idle operator (no clicks, no
  // tab refocus) never gets logged out when the short-lived token expires.
  // Reuses the rotating refresh flow; on failure we hand off to the same
  // session-expired path the 401 interceptor uses.
  const isAuthenticated = state.status === "authenticated";
  useEffect(() => {
    if (mode === "mock" || !isAuthenticated) return;

    let cancelled = false;
    const id = setInterval(() => {
      void (async () => {
        try {
          const token = await refreshAdminAccessToken();
          if (!cancelled && !token) notifyAdminSessionExpired();
        } catch {
          // Network blip — leave the session intact; the next interval (or a
          // real request's 401) will retry / expire as appropriate.
        }
      })();
    }, APP_CONFIG.adminTokenRefreshIntervalMs);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [mode, isAuthenticated]);

  useEffect(() => {
    // The ref guard guarantees the restore network call fires exactly once,
    // even though React 18 StrictMode mounts → unmounts → remounts this
    // effect in development. We deliberately do NOT use a `cancelled` flag
    // tied to cleanup: under StrictMode the first (and only) run would be
    // cancelled by the cleanup, then the remount's run is skipped by the
    // guard, so the result would never be applied and the provider would
    // hang in `loading` forever. Instead we apply the result via a
    // functional update that only writes while still `loading`, so a stale
    // late resolver can never clobber a newer login/logout.
    if (mode === "mock" || restoreStartedRef.current) return;
    restoreStartedRef.current = true;

    void (async () => {
      try {
        const session = await restoreAdminSession();
        if (session) {
          refreshTokenRef.current = getPersistedRefreshToken();
          resetAdminSessionExpiredNotified();
          setLoginRedirectReason("unauthorized");
          setState((prev) =>
            prev.status === "loading"
              ? { status: "authenticated", session }
              : prev,
          );
        } else {
          setState((prev) =>
            prev.status === "loading" ? { status: "unauthenticated" } : prev,
          );
        }
      } catch {
        clearAllTokens(refreshTokenRef);
        setState((prev) =>
          prev.status === "loading" ? { status: "unauthenticated" } : prev,
        );
      }
    })();
  }, [mode]);

  // Shared success tail: apply tokens, fetch the profile, promote the session.
  // Used by both the no-MFA login path and the MFA-verify path so the session
  // is established identically (and only ever AFTER real credentials succeed).
  const establishSession = useCallback(
    async (login: AdminLoginResponse) => {
      applyLoginTokens(login);
      refreshTokenRef.current = login.refresh_token ?? null;

      const me = await getAdminMe(login.access_token);
      resetAdminSessionExpiredNotified();
      setLoginRedirectReason("unauthorized");
      setState({ status: "authenticated", session: adminMeToSession(me) });
    },
    [],
  );

  const signInWithCredentials = useCallback(
    async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }): Promise<AdminSignInOutcome> => {
      if (mode === "mock") {
        setState({
          status: "authenticated",
          session: initialSession ?? MOCK_ADMIN_SESSION,
        });
        return { status: "authenticated" };
      }
      // We deliberately do NOT flip global state to "loading" here: the root
      // route renders a full-screen loader for "loading", which would unmount
      // the login page (and any in-flight MFA form). The login page shows its
      // own inline busy state instead. State only becomes "authenticated" once
      // real credentials (and MFA, when required) fully succeed.
      try {
        const result = await postAdminLogin({ email, password });
        if (isAdminMfaChallengeResponse(result)) {
          // Password OK, MFA required. No tokens, no session — hand the
          // challenge back so the caller can render the verification form.
          return {
            status: "mfa_required",
            challengeId: result.challenge_id,
            expiresIn: result.expires_in,
          };
        }
        await establishSession(result);
        return { status: "authenticated" };
      } catch (err) {
        clearAllTokens(refreshTokenRef);
        setState({ status: "unauthenticated" });
        throw err;
      }
    },
    [mode, initialSession, establishSession],
  );

  const completeMfaChallenge = useCallback(
    async ({ challengeId, code }: { challengeId: string; code: string }) => {
      if (mode === "mock") {
        // Mock mode never issues a real challenge; if a dev drives this path,
        // just seed the mock session. Dev-only, isolated from real mode.
        setState({
          status: "authenticated",
          session: initialSession ?? MOCK_ADMIN_SESSION,
        });
        return;
      }
      try {
        const login = await postAdminMfaVerify({
          challenge_id: challengeId,
          code,
        });
        await establishSession(login);
      } catch (err) {
        // Verification failed (bad/expired code) — no session is created. Keep
        // tokens cleared and rethrow so the caller keeps the challenge visible
        // and surfaces a safe error.
        clearAllTokens(refreshTokenRef);
        throw err;
      }
    },
    [mode, initialSession, establishSession],
  );

  const signOut = useCallback(async () => {
    const accessToken = getInMemoryAccessToken();
    const refreshToken =
      refreshTokenRef.current ?? getPersistedRefreshToken();
    if (mode === "real" && accessToken && refreshToken) {
      try {
        await postAdminLogout(accessToken, refreshToken);
      } catch {
        // Even if logout fails server-side, clear local state.
      }
    }
    clearAllTokens(refreshTokenRef);
    setLoginRedirectReason("unauthorized");
    setState({ status: "unauthenticated" });
  }, [mode]);

  const value = useMemo<AdminAuthContextValue>(() => {
    const session = state.status === "authenticated" ? state.session : null;
    return {
      state,
      session,
      loginRedirectReason,
      mode,
      signIn: (s) => {
        setState({ status: "authenticated", session: s });
      },
      signInWithCredentials,
      completeMfaChallenge,
      signOut,
      hasPermission: (permission) => hasPermission(session?.role ?? null, permission),
    };
  }, [
    state,
    loginRedirectReason,
    mode,
    signInWithCredentials,
    completeMfaChallenge,
    signOut,
  ]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside <AdminAuthProvider>");
  return ctx;
}

export function useAdminSession(): AdminSession | null {
  return useAdminAuth().session;
}
